import { CommonModule } from '@angular/common'
import type { OnInit } from '@angular/core'
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  viewChild,
} from '@angular/core'
import { FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Events } from '@ngrx/signals/events'
import { filter, map } from 'rxjs'

import { CustomFormsModule } from '@app/modules/forms/forms.module'
import { FormErrorService } from '@app/modules/forms/services/form-error.service'
import { PopupService } from '@app/modules/popup/popup.service'
import { Breakpoint2Service } from '@app/modules/ui/services/breakpoint-2.service'
import { UIModule } from '@app/modules/ui/ui.module'
import { MemberService } from '@app/services/member.service'
import { SiteStore } from '@app/store/site.store'

import type { Conversation } from '../../models/conversation.model'
import { FieldBoostComponent } from '../../modules/boost/components/field-boost/field-boost.component'
import type { MessagePhotoAlbumSection } from '../../modules/message-photo/models/api.model'
import { ConversationUIService } from '../../services/conversation-ui.service'
import { MessageCacheService } from '../../services/message-cache.service'
import { FieldMessageComponent } from '../field-message/field-message.component'
import { ConversationStore } from '../store/conversation.store'
import { sendingEvents } from '../store/sending.events'
import { formValueToSendMessagesPayload } from '../store/sending.feature.utils'

const MAX_MESSAGE_LENGTH = 1000

@UntilDestroy()
@Component({
  selector: 'sb-message-form',
  templateUrl: './message-form.component.html',
  styleUrls: ['./message-form.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CustomFormsModule,
    UIModule,
    FieldMessageComponent,
    FieldBoostComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessageFormComponent implements OnInit {
  private readonly nonNullableFormBuilder = inject(NonNullableFormBuilder)
  readonly conversationStore = inject(ConversationStore)
  readonly memberService = inject(MemberService)
  private readonly messageCacheService = inject(MessageCacheService)
  readonly breakpoint2Service = inject(Breakpoint2Service)
  private readonly conversationUIService = inject(ConversationUIService)
  private readonly injector = inject(Injector)
  private readonly popupService = inject(PopupService)
  private readonly events = inject(Events)
  readonly siteStore = inject(SiteStore)

  readonly humanize = FormErrorService.humanizeFactory('Message')

  readonly conversation = input.required<Conversation>()
  readonly attachPhotoButton = viewChild<unknown, ElementRef<HTMLElement>>('attachPhotoButton', { read: ElementRef })
  readonly fieldMessage = viewChild(FieldMessageComponent)

  readonly form = this.nonNullableFormBuilder.group({
    body: ['', [Validators.maxLength(MAX_MESSAGE_LENGTH)]],
    boosted: false,
    request_or_share_access: false,
  })

  hideGreeting = false
  cachedPrefill = ''

  private readonly passFormDisabled = effect(() => {
    if (!this.conversation().allow_chat) {
      this.form.disable({ emitEvent: false })
    } else {
      this.form.enable({ emitEvent: false })
    }
  })

  ngOnInit(): void {
    this.cachedPrefill = this.messageCacheService.getCachedMessage(this.conversation().profile).body

    this.form.patchValue({
      body: this.cachedPrefill || this.conversation().greeting_prefill || '',
      request_or_share_access: this.memberService.account.share_secret_albums_default
        ? this.conversationStore.canRequestOrShareAccess()
        : false,
    })

    this.form.controls.body.valueChanges.pipe(untilDestroyed(this)).subscribe(value => this.onMessageChange(value))
    this.form.controls.boosted.valueChanges.pipe(untilDestroyed(this)).subscribe(value => this.onBoostChange(value))

    this.events
      .on(sendingEvents.sendMessageSuccess)
      .pipe(
        filter(({ payload }) => payload.messages.some(message => message.type === 'text')),
        untilDestroyed(this),
      )
      .subscribe(() => this.form.reset({}, { emitEvent: false }))

    this.events
      .on(sendingEvents.sendMessageFailure)
      .pipe(
        map(({ payload }) => payload),
        untilDestroyed(this),
      )
      .subscribe(({ error }) => {
        FormErrorService.handleFormErrorResponse(this.form, error.error)
        this.clearFocus()
      })

    const voice = this.conversationStore.voice()
    if (voice) {
      this.conversationStore.openVoiceCreator(this.conversation(), this.injector, this.form.getRawValue(), voice)
    }
  }

  onMessageInputMouseDown(event: MouseEvent): void {
    if (this.popupService.getPopupRequest('message-boost-new-intro')) {
      event.preventDefault() // to prevent keyboard open on mobile
      this.conversationUIService.openNewMessageBoostModal(this.conversation())
    }
  }

  private onBoostChange(value: boolean): void {
    if (value && this.popupService.getPopupRequest('message-boost-new-intro')) {
      this.conversationUIService.openNewMessageBoostModal(this.conversation())
    }
  }

  private onMessageChange(value: string): void {
    this.hideGreeting = true
    this.conversationStore.sendTyping()
    this.messageCacheService.saveCachedMessage(this.conversation().profile, { body: value })
  }

  clearGreeting(): void {
    this.hideGreeting = true
    this.form.controls.body.reset('', { emitEvent: false })
  }

  onAttachPhotoButtonClick(): void {
    this.openMessagePhotoAlbum('approved')
  }

  private openMessagePhotoAlbum(initialSection: MessagePhotoAlbumSection): void {
    if (this.popupService.getPopupRequest('message-photos-rules')) {
      const instance = this.conversationUIService.openMessagePhotoAlbumRulesModal(this.conversation())
      instance.onConfirm(() => this.openMessagePhotoAlbum(initialSection))
      return
    }

    this.conversationUIService.openMessagePhotoAlbumModal(
      { initialSection, conversation: this.conversation(), messageForm: this.form.getRawValue() },
      this.injector,
    )
  }

  private clearFocus(): void {
    if (!(document.activeElement instanceof HTMLElement)) {
      return
    }
    document.activeElement.blur()
  }

  onSend(conversation: Conversation): void {
    this.hideGreeting = true
    const { payload, fakeMessages } = formValueToSendMessagesPayload(this.form.getRawValue())
    this.conversationStore.attemptToSendMessage(conversation, payload, fakeMessages)
  }

  onRecord(conversation: Conversation): void {
    this.conversationStore.openVoiceCreator(conversation, this.injector, this.form.getRawValue())
  }
}
